import { formatName, formatDate as format } from '../lib/format';
import LoadingPopup from './LoadingPopup';
import { toast } from "react-toastify";
import { useEffect, useState } from "react";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import { getQuarterHoursFromTimes } from "../common";
import { resolvePdfGraphic } from "./pdfMedia";
import { getAllocationDisplayAddress } from '../utils/workerAllocationAddress';
import { runPdf } from '../utils/pdfSettle';
pdfMake.vfs = pdfFonts['Roboto-Regular.ttf'];


const TimesheetPdfGenerator = ({ timesheet, workerName, logo, onComplete }) => {
    const [failed, setFailed] = useState(false);
    useEffect(() => {
        if (timesheet) {
            setFailed(false);
            generatePdf().catch(error => {
                setFailed(true);
                toast.error(error?.message || "Unable to download timesheet.");
                // The parent clears its busy state here; without it a failed
                // download leaves the page's buttons disabled.
                if (onComplete) onComplete();
            });
        }
    }, [timesheet]);

    const parseBreakMinutes = (value) => {
        if (value === null || value === undefined || value === "") return 0;
        if (typeof value === "number" && Number.isFinite(value)) return value;
        const match = String(value).match(/(\d+(\.\d+)?)/);
        return match ? Number(match[1]) : 0;
    };

    const formatDateTime = (value) => {
        if (!value) return "-";
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return "-";
        return format(date, "dd/MMM/yyyy h:mm a");
    };

    const formatDateOnly = (value) => {
        if (!value) return "-";
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return "-";
        return format(date, "dd/MMM/yyyy");
    };

    const calculateTotalHours = (start, end, breakValue) => {
        if (!start || !end) return "-";
        const startDate = new Date(start);
        const endDate = new Date(end);
        if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return "-";
        const hours = getQuarterHoursFromTimes({
            clockIn: startDate,
            clockOut: endDate,
            breakMinutes: parseBreakMinutes(breakValue),
            breakIncludedInPay: false,
        });
        return hours === "" ? "-" : hours.toFixed(2);
    };

    const formatLocationCoordinates = (coords) => {
        if (!Array.isArray(coords) || coords.length < 2) return null;
        const [lat, lng] = coords;
        if (!Number.isFinite(Number(lat)) || !Number.isFinite(Number(lng))) return null;
        return `${Number(lat).toFixed(6)}, ${Number(lng).toFixed(6)}`;
    };

    const formatWorkerDisplayName = (name, role) => {
        const trimmedName = formatName(name).trim();
        const trimmedRole = String(role || '').trim();
        if (!trimmedRole) return trimmedName || "-";
        return `${trimmedName || "-"} (${trimmedRole})`;
    };

    const generatePdf = async () => {
        const logoGraphic = await resolvePdfGraphic(logo);
        const locationLabel = formatLocationCoordinates(timesheet.locationCoordinates);
        const locationLink = locationLabel ? `https://www.google.com/maps?q=${timesheet.locationCoordinates[0]},${timesheet.locationCoordinates[1]}` : "";
        const workerLabel = formatWorkerDisplayName(workerName, timesheet.workerCapacity);
        const workerList = Array.isArray(timesheet.chosenWorkerNames) ? timesheet.chosenWorkerNames : [];

        const signatureGraphic = timesheet.supervisorSign
            ? await resolvePdfGraphic(timesheet.supervisorSign)
            : null;

        const supervisorSignatureCell = timesheet.supervisorNotAvailable
            ? { text: "Supervisor not available", italics: true }
            : signatureGraphic
                ? { ...signatureGraphic, fit: [100, 35] }
                : { text: "Not provided" };

        const docDefinition = {
            content: [
                {
                    columns: [
                        logoGraphic
                            ? { ...logoGraphic, fit: [120, 60], margin: [0, 0, 0, 10] }
                            : { text: " " },
                        {
                            stack: [
                                { text: "TIMESHEET", style: "title", alignment: "right" },
                                {
                                    text: [
                                        { text: "Shift Date: ", bold: true },
                                        `${formatDateOnly(timesheet.date)}\n`,
                                        { text: "Generated: ", bold: true },
                                        `${formatDateTime(new Date())}\n`,
                                    ],
                                    alignment: "right",
                                    margin: [0, 5, 0, 0],
                                },
                            ],
                        },
                    ],
                },
                { text: " " },
                {
                    columns: [
                        [
                            { text: workerLabel, bold: true },
                            { text: "Worker" },
                        ],
                        [
                            { text: "Site Details", bold: true, margin: [0, 0, 0, 2] },
                            { text: timesheet.clientName || "-" },
                            { text: timesheet.siteName || "-" },
                            { text: getAllocationDisplayAddress(timesheet) },
                        ],
                    ],
                },
                { text: "\n" },
                { text: "Workers", style: "sectionTitle" },
                {
                    ul: workerList.length
                        ? workerList.map(worker => formatWorkerDisplayName(worker.name, worker.role))
                        : ["-"],
                    margin: [0, 8, 0, 16],
                },
                { text: "Shift Details", style: "sectionTitle" },
                {
                    table: {
                        widths: ["auto", "*"],
                        body: [
                            [{ text: "Clock In", style: "tableHeader" }, { text: formatDateTime(timesheet.clockIn) }],
                            [{ text: "Clock Out", style: "tableHeader" }, { text: formatDateTime(timesheet.clockOut) }],
                            [{ text: "Break", style: "tableHeader" }, { text: `${parseBreakMinutes(timesheet.breakMinutes)} mins` }],
                            [{ text: "Total Hours", style: "tableHeader" }, { text: calculateTotalHours(timesheet.clockIn, timesheet.clockOut, timesheet.breakMinutes) }],
                            [{
                                text: "Worker Location",
                                style: "tableHeader"
                            }, locationLabel ? {
                                text: locationLabel,
                                link: locationLink,
                                color: "#1d4ed8",
                                decoration: "underline",
                            } : { text: "-" }],
                        ],
                    },
                    layout: "lightHorizontalLines",
                    margin: [0, 8, 0, 16],
                },
                { text: "Supervisor Sign Off", style: "sectionTitle" },
                {
                    table: {
                        widths: ["auto", "*"],
                        body: [
                            [{ text: "Supervisor Name", style: "tableHeader" }, { text: formatName(timesheet.supervisorName || "-") }],
                            [{ text: "Signature", style: "tableHeader" }, supervisorSignatureCell],
                        ],
                    },
                    layout: "lightHorizontalLines",
                },
            ],
            styles: {
                title: {
                    fontSize: 18,
                    bold: true,
                },
                sectionTitle: {
                    fontSize: 12,
                    bold: true,
                },
                tableHeader: {
                    fillColor: "#f2f2f2",
                    bold: true,
                },
            },
            defaultStyle: {
                fontSize: 10,
            },
        };

        const fileLabel = timesheet.date ? formatDateOnly(timesheet.date).replace(/\//g, "-") : "timesheet";
        await runPdf(done => pdfMake.createPdf(docDefinition).download(`timesheet_${fileLabel}.pdf`, done));
        if (onComplete) onComplete();
    };

    return <LoadingPopup visible={!!timesheet && !failed} message="Downloading timesheet..." />;
};

export default TimesheetPdfGenerator;
